import { Link, useLocation } from "react-router-dom"
import { Lock } from "lucide-react"
import { cn } from "../../utils/cn"
import { useState, useEffect, useRef, useMemo } from "react"
import { useSections } from "../../contexts/SectionContext"

export interface NavigationStep {
    id: string | number
    title: string
    href: string
    disabled?: boolean
    locked?: boolean
    sections?: Array<{
        id?: string
        title: string
    }>
}

interface GuideNavProps {
    variant?: "sidebar" | "overview"
    steps?: NavigationStep[]
}

export function GuideNav({ variant = "sidebar", steps = [] }: GuideNavProps) {
    const location = useLocation()
    const pathname = location.pathname
    const [activeSection, setActiveSection] = useState<string | null>(null)
    const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    // Attempt to get section context (may be null if not in provider)
    const context = useSections();
    const contextSections = context?.sections || [];
    const contextActiveId = context?.activeId;

    // Use context active ID if available, otherwise local state
    // We only use context active ID if we have registered sections
    const effectiveActiveSection = (contextSections.length > 0 && contextActiveId)
        ? contextActiveId
        : activeSection;

    // We rely entirely on SectionContext for active section tracking and scrolling.
    // No manual scroll listeners or offset calculations here.

    const handleSectionClick = (sectionId: string) => {
        if (context) {
            context.scrollToSection(sectionId);
        }
    }

    // Memoize the simplified sections list to avoid re-mapping on every render (e.g. scroll updates)
    const contextSectionsSimple = useMemo(() =>
        contextSections.map(s => ({ id: s.id, title: s.title })),
        [contextSections]);

    return (
        <nav className={variant === 'overview' ? 'space-y-1 px-4' : 'space-y-1'}>
            {steps.map((step) => {
                const isActive = pathname === step.href
                const isLocked = step.locked

                // Determine sections source: Context (auto-generated) or Static Props
                // Only use context sections for the ACTIVE step
                let displaySections = step.sections;
                if (isActive && contextSections.length > 0) {
                    displaySections = contextSectionsSimple;
                }

                const hasSections = displaySections && displaySections.length > 0
                // Only expand if active, has sections, and NOT locked
                const shouldExpand = isActive && !isLocked && hasSections

                if (step.disabled) {
                    return (
                        <div key={step.id} className="flex items-center gap-3 py-3 text-b6/40 cursor-not-allowed">
                            <div className="w-8 h-8 rounded-full bg-b02 flex items-center justify-center flex-shrink-0">
                                <span className="text-sm font-medium">{step.id}</span>
                            </div>
                            <span className="text-sm">{step.title}</span>
                        </div>
                    )
                }

                return (
                    <div key={step.id}>
                        <Link
                            to={step.href}
                            className={cn(
                                "group flex items-center gap-3 py-3 transition-all duration-200",
                                isLocked && !isActive && "opacity-50",
                            )}
                        >
                            <div
                                className={cn(
                                    "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200",
                                    isActive
                                        ? "bg-b text-w"
                                        : "bg-b1 text-b6 group-hover:bg-b2 group-hover:text-b",
                                )}
                            >
                                <span className={cn("text-sm", isActive ? "font-bold" : "font-medium")}>{step.id}</span>
                            </div>
                            <div className="flex-1 flex items-center justify-between gap-2">
                                <span
                                    className={cn(
                                        "text-sm transition-colors duration-200",
                                        isActive ? "font-bold text-b" : "font-normal text-b6 group-hover:text-b",
                                    )}
                                >
                                    {step.title}
                                </span>
                                {isLocked && (
                                    <Lock
                                        className={cn(
                                            "w-4 h-4 flex-shrink-0 transition-colors duration-200",
                                            isActive ? "text-b" : "text-b6 group-hover:text-b",
                                        )}
                                    />
                                )}
                            </div>
                        </Link>

                        <div
                            className={cn(
                                "grid transition-[grid-template-rows,opacity] duration-200 ease-out",
                                shouldExpand ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
                            )}
                        >
                            <div className={cn("overflow-hidden", shouldExpand && "mt-1 mb-2")}>
                                <div className="ml-11 space-y-1">
                                    {displaySections?.map((section) => {
                                        if (!section.id) return null
                                        const isSectionActive = effectiveActiveSection === section.id

                                        return (
                                            <button
                                                key={section.id}
                                                type="button"
                                                onClick={() => section.id && handleSectionClick(section.id)}
                                                className={cn(
                                                    "w-full text-left py-2 px-3 rounded-lg text-sm transition-all duration-200",
                                                    isSectionActive
                                                        ? "bg-b05 text-b font-medium"
                                                        : "text-b6 hover:bg-b02 hover:text-b",
                                                )}
                                            >
                                                {section.title}
                                            </button>
                                        )
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                )
            })}
        </nav>
    )
}
